import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { useStats } from '@/hooks/useStats';
import { Product } from '@/mocks/products';
import PanelLogin from './components/PanelLogin';
import MultiImageUploader from './components/MultiImageUploader';

type Tab = 'list' | 'add' | 'slider' | 'stats';

const emptyForm = { name: '', description: '', price: 0, image: '', images: [] as string[], mainImageIndex: 0, category: 'Laptop' };
const categories = ['Laptop', 'Masaüstü', 'Monitör', 'İşlemci', 'Anakart', 'RAM', 'SSD', 'Ekran Kartı', 'Cep Telefonu', 'Aksesuar', 'Diğer'];

const PanelPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { products, loading, addProduct, updateProduct, deleteProduct, toggleSlider, reorderProducts } = useProducts();
  const { getSortedStats, deleteStat } = useStats();
  const [tab, setTab] = useState<Tab>('list');
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'success' | 'error'>('success');
  const [dragId, setDragId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteStatId, setDeleteStatId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const auth = sessionStorage.getItem('panel_auth');
    if (auth === 'true') setIsAuthenticated(true);
  }, []);

  const sliderProducts = products.filter(p => p.inSlider).sort((a, b) => a.sliderOrder - b.sliderOrder);
  const sortedStats = getSortedStats();
  const maxViews = sortedStats.length > 0 ? sortedStats[0].views : 1;

  const showMsg = (text: string, type: 'success' | 'error' = 'success') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(''), 3000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('panel_auth');
    setIsAuthenticated(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validImages = (form.images || []).filter(Boolean);
    const mainIdx = form.mainImageIndex ?? 0;
    const mainImg = validImages[mainIdx] ?? validImages[0] ?? form.image;
    if (!mainImg) { showMsg('En az bir fotoğraf yükleyin.', 'error'); return; }
    const payload = { ...form, price: Number(form.price), images: validImages, mainImageIndex: mainIdx, image: mainImg };
    if (editId) {
      updateProduct(editId, payload);
      showMsg('Ürün başarıyla güncellendi!');
      setEditId(null);
    } else {
      addProduct({ ...payload, inSlider: false, sliderOrder: 0 });
      showMsg('Ürün başarıyla eklendi!');
    }
    setForm(emptyForm);
    setTab('list');
  };

  const startEdit = (p: Product) => {
    const imgs = p.images && p.images.length > 0 ? p.images : [p.image];
    setForm({ name: p.name, description: p.description, price: p.price, image: p.image, images: imgs, mainImageIndex: p.mainImageIndex ?? 0, category: p.category });
    setEditId(p.id);
    setTab('add');
  };

  const handleDelete = (id: string) => {
    deleteProduct(id);
    setDeleteConfirmId(null);
    showMsg('Ürün silindi.');
  };

  const handleToggleSlider = (id: string) => {
    const result = toggleSlider(id);
    if (result?.error) showMsg(result.error, 'error');
  };

  const handleDragStart = (id: string) => setDragId(id);
  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const sorted = [...products].sort((a, b) => a.order - b.order);
    const fromIdx = sorted.findIndex(p => p.id === dragId);
    const toIdx = sorted.findIndex(p => p.id === targetId);
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    reorderProducts(reordered);
  };

  const filteredProducts = [...products]
    .sort((a, b) => a.order - b.order)
    .filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase())
    );

  if (!isAuthenticated) {
    return <PanelLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500">
            <i className="ri-settings-3-line text-white text-base"></i>
          </div>
          <span className="font-bold text-base">Yönetim Paneli</span>
          <span className="hidden sm:inline text-slate-500 text-xs">· İkinci El Ürün Yönetimi</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-300 hover:text-white text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap">
            <i className="ri-home-line"></i>
            <span className="hidden sm:inline">Siteye Dön</span>
          </Link>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-400 text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-logout-box-line"></i>
            <span className="hidden sm:inline">Çıkış</span>
          </button>
        </div>
      </div>

      {/* Toast */}
      {msg && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${msgType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          <i className={msgType === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          {msg}
        </div>
      )}

      {/* Delete Product Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-center font-bold text-slate-900 mb-2">Ürünü Sil</h3>
            <p className="text-center text-slate-500 text-sm mb-5">Bu ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 border border-slate-200 rounded-lg py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2.5 text-sm font-semibold cursor-pointer whitespace-nowrap">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Stat Confirm Modal */}
      {deleteStatId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-100 mx-auto mb-4">
              <i className="ri-bar-chart-line text-orange-500 text-xl"></i>
            </div>
            <h3 className="text-center font-bold text-slate-900 mb-2">İstatistiği Sil</h3>
            <p className="text-center text-slate-500 text-sm mb-5">Bu ürünün görüntülenme istatistiği silinecek. (Ürün listeden kaldırıldıysa istatistiği temizleyebilirsiniz.)</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteStatId(null)} className="flex-1 border border-slate-200 rounded-lg py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={() => { deleteStat(deleteStatId); setDeleteStatId(null); showMsg('İstatistik silindi.'); }} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-2.5 text-sm font-semibold cursor-pointer whitespace-nowrap">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Toplam Ürün', value: products.length, icon: 'ri-box-3-line', color: 'bg-slate-100 text-slate-700' },
            { label: "Slider'da", value: sliderProducts.length, icon: 'ri-slideshow-line', color: 'bg-orange-100 text-orange-600' },
            { label: 'Boş Slot', value: 10 - sliderProducts.length, icon: 'ri-add-circle-line', color: 'bg-green-100 text-green-600' },
            { label: 'Toplam Görüntülenme', value: sortedStats.reduce((s, x) => s + x.views, 0), icon: 'ri-eye-line', color: 'bg-amber-100 text-amber-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.color}`}>
                <i className={`${s.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-slate-900 leading-none">{s.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-xl p-1 border border-slate-200 w-fit">
          {([
            ['list', 'ri-list-check', 'Ürün Listesi'],
            ['add', 'ri-add-circle-line', editId ? 'Ürün Düzenle' : 'Ürün Ekle'],
            ['slider', 'ri-slideshow-line', 'Slider Yönetimi'],
            ['stats', 'ri-bar-chart-2-line', 'İstatistikler'],
          ] as [Tab, string, string][]).map(([t, icon, label]) => (
            <button
              key={t}
              onClick={() => { setTab(t); if (t !== 'add') { setEditId(null); setForm(emptyForm); } }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <i className={icon}></i>
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* ── ÜRÜN LİSTESİ ── */}
        {tab === 'list' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h2 className="text-xl font-extrabold text-slate-900">Ürünler ({filteredProducts.length})</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Ürün ara..." className="border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-orange-400 w-44" />
                </div>
                <button onClick={() => setTab('add')} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line"></i> Yeni Ürün
                </button>
              </div>
            </div>
            <p className="text-slate-400 text-xs mb-4 flex items-center gap-1"><i className="ri-drag-move-line"></i> Sıralamayı değiştirmek için sürükleyip bırakın.</p>
            <div className="space-y-2">
              {filteredProducts.map(p => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => handleDragStart(p.id)}
                  onDragOver={e => handleDragOver(e, p.id)}
                  onDragEnd={() => setDragId(null)}
                  className={`bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3 cursor-grab active:cursor-grabbing transition-all ${dragId === p.id ? 'opacity-40 scale-95' : 'hover:border-slate-200'}`}
                >
                  <i className="ri-drag-move-line text-slate-300 text-lg flex-shrink-0"></i>
                  <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 text-sm truncate">{p.name}</p>
                    <p className="text-slate-400 text-xs truncate">{p.category} · {p.price.toLocaleString('tr-TR')} ₺</p>
                    <p className="text-slate-300 text-xs truncate hidden sm:block">{p.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => handleToggleSlider(p.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${p.inSlider ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                      <i className={`${p.inSlider ? 'ri-slideshow-fill' : 'ri-slideshow-line'} mr-1`}></i>
                      <span className="hidden sm:inline">{p.inSlider ? "Slider'da" : "Slider'a Ekle"}</span>
                    </button>
                    <button onClick={() => startEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 cursor-pointer">
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button onClick={() => setDeleteConfirmId(p.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 cursor-pointer">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="text-center py-16 text-slate-400">
                  <i className="ri-search-line text-4xl mb-3 block"></i>
                  <p>Arama sonucu bulunamadı.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ÜRÜN EKLE / DÜZENLE ── */}
        {tab === 'add' && (
          <div className="max-w-2xl">
            <h2 className="text-xl font-extrabold text-slate-900 mb-6">{editId ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h2>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ürün Adı *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="Ürün adını girin" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Fiyat (₺) *</label>
                  <input required type="number" min="0" value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Kategori *</label>
                  <select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 cursor-pointer">
                    {categories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <MultiImageUploader
                  images={form.images || []}
                  mainImageIndex={form.mainImageIndex ?? 0}
                  onChange={(imgs, mainIdx) => setForm({ ...form, images: imgs, mainImageIndex: mainIdx, image: imgs[mainIdx] ?? imgs[0] ?? '' })}
                  maxImages={5}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama *</label>
                <textarea required rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 resize-none" placeholder="Ürün özellikleri, kondisyon bilgisi..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-slate-900 hover:bg-orange-500 text-white py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap">{editId ? 'Güncelle' : 'Ürün Ekle'}</button>
                <button type="button" onClick={() => { setTab('list'); setEditId(null); setForm(emptyForm); }} className="px-5 py-2.5 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">İptal</button>
              </div>
            </form>
          </div>
        )}

        {/* ── SLIDER YÖNETİMİ ── */}
        {tab === 'slider' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold text-slate-900">Slider Ürünleri</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${sliderProducts.length >= 10 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>{sliderProducts.length}/10 Ürün</span>
            </div>
            {sliderProducts.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <i className="ri-slideshow-line text-5xl mb-3 block"></i>
                <p className="mb-4">Henüz slider ürünü yok.</p>
                <button onClick={() => setTab('list')} className="bg-orange-500 text-white px-5 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">Ürün Listesine Git</button>
              </div>
            ) : (
              <div className="space-y-3">
                {sliderProducts.map((p, i) => (
                  <div key={p.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 text-sm truncate">{p.name}</p>
                      <p className="text-slate-400 text-xs">{p.category} · {p.price.toLocaleString('tr-TR')} ₺</p>
                    </div>
                    <button onClick={() => handleToggleSlider(p.id)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer whitespace-nowrap">
                      <i className="ri-close-line mr-1"></i>Çıkar
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-700">
              <i className="ri-information-line mr-2"></i>
              Slider sıralamasını değiştirmek için <strong>Ürün Listesi</strong> sekmesinde sürükle-bırak kullanın.
            </div>
            {sliderProducts.length < 10 && (
              <div className="mt-6">
                <h3 className="font-bold text-slate-700 text-sm mb-3">Slider'a Eklenebilir Ürünler ({products.filter(p => !p.inSlider).length})</h3>
                <div className="space-y-2">
                  {products.filter(p => !p.inSlider).map(p => (
                    <div key={p.id} className="bg-white rounded-xl border border-dashed border-slate-200 p-3 flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{p.name}</p>
                        <p className="text-slate-400 text-xs">{p.category} · {p.price.toLocaleString('tr-TR')} ₺</p>
                      </div>
                      <button onClick={() => handleToggleSlider(p.id)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-100 text-orange-600 hover:bg-orange-200 cursor-pointer whitespace-nowrap">
                        <i className="ri-add-line mr-1"></i>Ekle
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── İSTATİSTİKLER ── */}
        {tab === 'stats' && (
          <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Ürün Görüntülenme İstatistikleri</h2>
                <p className="text-slate-400 text-sm mt-1">Ziyaretçilerin en çok ilgi gösterdiği ürünler</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {sortedStats.reduce((s, x) => s + x.views, 0)} toplam görüntülenme
                </span>
                <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                  {sortedStats.length} ürün
                </span>
              </div>
            </div>

            {sortedStats.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <i className="ri-bar-chart-line text-5xl mb-3 block"></i>
                <p className="font-medium mb-1">Henüz görüntülenme verisi yok</p>
                <p className="text-sm">Ziyaretçiler ürün sayfalarını ziyaret ettikçe burada görünecek.</p>
              </div>
            ) : (
              <>
                {/* Top 3 Podium */}
                {sortedStats.length >= 3 && (
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    {[sortedStats[1], sortedStats[0], sortedStats[2]].map((stat, podiumIdx) => {
                      const rank = podiumIdx === 0 ? 2 : podiumIdx === 1 ? 1 : 3;
                      const isInList = products.some(p => p.id === stat.id);
                      const configs = [
                        { bg: 'bg-slate-100', border: 'border-slate-200', badge: 'bg-slate-400', height: 'h-24', label: '2.' },
                        { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-400', height: 'h-32', label: '1.' },
                        { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-400', height: 'h-20', label: '3.' },
                      ];
                      const cfg = configs[podiumIdx];
                      return (
                        <div key={stat.id} className={`${cfg.bg} border ${cfg.border} rounded-xl p-3 flex flex-col items-center text-center relative`}>
                          <span className={`w-7 h-7 flex items-center justify-center rounded-full ${cfg.badge} text-white text-xs font-extrabold mb-2`}>{cfg.label}</span>
                          <p className="font-bold text-slate-900 text-xs line-clamp-2 mb-1">{stat.name}</p>
                          <p className="text-slate-400 text-xs mb-2">{stat.category}</p>
                          <span className="text-lg font-extrabold text-orange-500">{stat.views}</span>
                          <span className="text-xs text-slate-400">görüntülenme</span>
                          {!isInList && (
                            <span className="absolute top-2 right-2 text-xs bg-red-100 text-red-500 px-1.5 py-0.5 rounded-full">Yok</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Full List with animated bars */}
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-sm">Tüm Ürünler</span>
                    <span className="text-xs text-slate-400">Görüntülenmeye göre sıralı</span>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {sortedStats.map((stat, i) => {
                      const isInList = products.some(p => p.id === stat.id);
                      const barWidth = Math.max(3, Math.round((stat.views / maxViews) * 100));
                      const barColor = i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-orange-400' : 'bg-orange-300';
                      const rankBg = i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-300 text-slate-700' : i === 2 ? 'bg-orange-300 text-white' : 'bg-slate-100 text-slate-500';
                      return (
                        <div key={stat.id} className={`px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors duration-150 ${!isInList ? 'bg-red-50/30' : ''}`}>
                          <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${rankBg}`}>
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <p className="font-bold text-slate-900 text-sm truncate">{stat.name}</p>
                              {!isInList && (
                                <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">Listede Yok</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs text-slate-400">{stat.category}</span>
                              <span className="text-slate-200">·</span>
                              <span className="text-xs text-slate-400">Son: {new Date(stat.lastViewed).toLocaleDateString('tr-TR')}</span>
                            </div>
                            {/* Animated bar */}
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className={`${barColor} h-2.5 rounded-full transition-all duration-1000`}
                                  style={{ width: `${barWidth}%` }}
                                />
                              </div>
                              <span className="text-sm font-extrabold text-slate-700 whitespace-nowrap w-24 text-right">
                                {stat.views.toLocaleString('tr-TR')} <span className="text-xs font-normal text-slate-400">görüntülenme</span>
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setDeleteStatId(stat.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 transition-colors duration-150"
                            title="İstatistiği sil"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-500 flex items-start gap-2">
              <i className="ri-information-line text-slate-400 mt-0.5 flex-shrink-0"></i>
              <span>Ürün listeden kaldırıldıysa <strong className="text-red-400">Listede Yok</strong> olarak işaretlenir. Çöp kutusu ikonuyla istatistiği temizleyebilirsiniz.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelPage;
